import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import Drawer from '../../components/Drawer';
import { ClipboardList, Plus, Search, ChevronLeft, ChevronRight, X } from 'lucide-react';

const FEE_TYPES = ['tuition', 'subscription', 'admission', 'transport', 'other'];
const FREQUENCIES = ['monthly', 'one-time'];

function StatusPill({ status }) {
  const cls = status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200';
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase border ${cls}`}>{status || 'active'}</span>;
}



function NewFeePlanDrawer({ open, onClose, token, onSuccess, formatMoney, currencySymbol }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', description: '', billing_day: '1', active_status: 'active' });
  const [items, setItems] = useState([{ fee_type: 'tuition', frequency: 'monthly', amount: '', due_day: '5', tax_rate: '0' }]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      setForm({ name: '', description: '', billing_day: '1', active_status: 'active' });
      setItems([{ fee_type: 'tuition', frequency: 'monthly', amount: '', due_day: '5', tax_rate: '0' }]);
    }
  }, [open]);

  const setItem = (i, k, v) => setItems(prev => { const c = [...prev]; c[i] = { ...c[i], [k]: v }; return c; });
  const addItem = () => setItems(prev => [...prev, { fee_type: 'other', frequency: 'monthly', amount: '', due_day: '5', tax_rate: '0' }]);
  const removeItem = i => setItems(prev => prev.filter((_, j) => j !== i));

  const submit = async () => {
    if (!form.name.trim()) { toast.show('Plan name is required', 'error'); return; }
    if (items.length === 0) { toast.show('Add at least one fee item', 'error'); return; }
    for (const it of items) {
      if (!it.amount || parseFloat(it.amount) <= 0) { toast.show('All items need a valid amount', 'error'); return; }
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        billing_day: parseInt(form.billing_day) || 1,
        active_status: form.active_status,
        items: items.map(it => ({
          fee_type: it.fee_type,
          frequency: it.frequency,
          amount: parseFloat(it.amount),
          due_day: it.frequency === 'monthly' ? (parseInt(it.due_day) || 5) : undefined,
          tax_rate: parseFloat(it.tax_rate) || 0,
        })),
      };
      const r = await api.feePlanCreate(token, body);
      toast.show('Fee plan created', 'success');
      onSuccess(r);
    } catch (e) { toast.show(e?.message || 'Create failed', 'error'); } finally { setSaving(false); }
  };

  return (
    <Drawer open={open} onClose={onClose} title="New Fee Plan" wide>
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <label className="block col-span-2"><span className="text-xs font-bold text-gray-500 uppercase">Plan Name *</span>
            <input data-testid="nfp-name" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Standard Monthly Plan" /></label>
          <label className="block col-span-2"><span className="text-xs font-bold text-gray-500 uppercase">Description</span>
            <input data-testid="nfp-desc" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.description} onChange={e => set('description', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Billing Day (1-28)</span>
            <input data-testid="nfp-billing-day" type="number" min="1" max="28" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.billing_day} onChange={e => set('billing_day', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Status</span>
            <select data-testid="nfp-status" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-200" value={form.active_status} onChange={e => set('active_status', e.target.value)}>
              <option value="active">Active</option><option value="inactive">Inactive</option>
            </select></label>
        </div>

        <div data-testid="nfp-items-section">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-gray-500 uppercase">Fee Items</span>
            <button onClick={addItem} data-testid="nfp-add-item" className="flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800"><Plus size={14} /> Add Item</button>
          </div>
          {items.map((it, i) => (
            <div key={i} data-testid={`nfp-item-${i}`} className="grid grid-cols-12 gap-2 mb-3 p-3 bg-gray-50 rounded-xl relative items-end">
              {items.length > 1 && <button onClick={() => removeItem(i)} className="absolute top-2 right-2 p-1 rounded hover:bg-gray-200 text-gray-400"><X size={14} /></button>}
              <label className="col-span-3 block"><span className="text-[10px] font-bold text-gray-400 uppercase">Type</span>
                <select data-testid={`nfp-item-type-${i}`} className="mt-0.5 w-full px-2 py-2 rounded-lg border border-gray-200 text-sm bg-white capitalize" value={it.fee_type} onChange={e => setItem(i, 'fee_type', e.target.value)}>
                  {FEE_TYPES.map(ft => <option key={ft} value={ft}>{ft.charAt(0).toUpperCase() + ft.slice(1)}</option>)}
                </select></label>
              <label className="col-span-2 block"><span className="text-[10px] font-bold text-gray-400 uppercase">Frequency</span>
                <select data-testid={`nfp-item-freq-${i}`} className="mt-0.5 w-full px-2 py-2 rounded-lg border border-gray-200 text-sm bg-white" value={it.frequency} onChange={e => setItem(i, 'frequency', e.target.value)}>
                  {FREQUENCIES.map(f => <option key={f} value={f}>{f === 'one-time' ? 'One-time' : 'Monthly'}</option>)}
                </select></label>
              <label className="col-span-2 block"><span className="text-[10px] font-bold text-gray-400 uppercase">Amount ({currencySymbol})</span>
                <input data-testid={`nfp-item-amount-${i}`} type="number" className="mt-0.5 w-full px-2 py-2 rounded-lg border border-gray-200 text-sm" value={it.amount} onChange={e => setItem(i, 'amount', e.target.value)} placeholder="0" /></label>
              {it.frequency === 'monthly' && (
                <label className="col-span-2 block"><span className="text-[10px] font-bold text-gray-400 uppercase">Due Day</span>
                  <input data-testid={`nfp-item-due-${i}`} type="number" min="1" max="28" className="mt-0.5 w-full px-2 py-2 rounded-lg border border-gray-200 text-sm" value={it.due_day} onChange={e => setItem(i, 'due_day', e.target.value)} /></label>
              )}
              <label className={`${it.frequency === 'monthly' ? 'col-span-2' : 'col-span-4'} block`}><span className="text-[10px] font-bold text-gray-400 uppercase">Tax %</span>
                <input data-testid={`nfp-item-tax-${i}`} type="number" className="mt-0.5 w-full px-2 py-2 rounded-lg border border-gray-200 text-sm" value={it.tax_rate} onChange={e => setItem(i, 'tax_rate', e.target.value)} placeholder="0" /></label>
            </div>
          ))}
        </div>

        <button onClick={submit} disabled={saving} data-testid="nfp-submit"
          className="w-full h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-sm disabled:opacity-50 hover:shadow-lg transition-all">
          {saving ? 'Creating...' : 'Create Fee Plan'}
        </button>
      </div>
    </Drawer>
  );
}

export default function FeePlans() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { formatMoney, currencySymbol } = useTenantFormat();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 25;
  const [newOpen, setNewOpen] = useState(false);
  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await api.feePlansList(token, { q: search || undefined, active_status: statusFilter || undefined, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
      setItems(r.items || r || []);
      setTotal(r.total ?? (r.items || r || []).length);
    } catch (e) {
      if (e?.status === 403) return;
      setItems([]);
    } finally { setLoading(false); }
  }, [token, search, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="fee-plans-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 flex items-center gap-3"><ClipboardList size={28} className="text-indigo-600" /> Fee Plans</h1>
          <p className="text-gray-500 text-sm mt-1">{total} plan{total !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setNewOpen(true)} data-testid="new-fee-plan-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-bold shadow-lg hover:shadow-xl hover:brightness-110 transition-all">
          <Plus size={16} /> New Fee Plan
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="feeplan-search" placeholder="Search by name..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none" />
        </div>
        <select data-testid="feeplan-status-filter" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white text-gray-700 font-semibold">
          <option value="">All Status</option><option value="active">Active</option><option value="inactive">Inactive</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="fee-plans-empty">
          <div className="w-20 h-20 rounded-2xl bg-indigo-50 flex items-center justify-center mb-5"><ClipboardList size={36} className="text-indigo-400" /></div>
          <h3 className="text-gray-900 font-bold text-lg mb-2">No fee plans yet</h3>
          <p className="text-gray-500 text-sm mb-5">Create your first fee plan to start billing students automatically.</p>
          <button onClick={() => setNewOpen(true)} className="px-5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700"><Plus size={14} className="inline mr-1.5" />Create Fee Plan</button>
        </div>
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden" data-testid="fee-plans-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Plan Name</th>
                <th className="text-center px-4 py-3 text-gray-500 text-xs font-bold uppercase">Items</th>
                <th className="text-center px-4 py-3 text-gray-500 text-xs font-bold uppercase">Billing Day</th>
                <th className="text-center px-4 py-3 text-gray-500 text-xs font-bold uppercase">Assigned</th>
                <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Status</th>
              </tr></thead>
              <tbody>
                {items.map(fp => (
                  <tr key={fp.id} data-testid={`feeplan-row-${fp.id}`} onClick={() => navigate(`/school/fee-plans/${fp.id}`)}
                    className="border-b border-gray-100 last:border-0 hover:bg-indigo-50/40 cursor-pointer transition-colors h-[52px]">
                    <td className="px-5 py-3">
                      <p className="text-gray-900 font-semibold">{fp.name}</p>
                      {fp.description && <p className="text-gray-400 text-xs mt-0.5 truncate max-w-[260px]">{fp.description}</p>}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700 tabular-nums">{(fp.items || []).length}</td>
                    <td className="px-4 py-3 text-center text-gray-700 tabular-nums">{fp.billing_day || '—'}</td>
                    <td className="px-4 py-3 text-center text-gray-700 tabular-nums">{fp.assigned_count ?? '—'}</td>
                    <td className="px-4 py-3"><StatusPill status={fp.active_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-1">
              <p className="text-gray-500 text-xs">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50"><ChevronLeft size={14} className="inline" /> Prev</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50">Next <ChevronRight size={14} className="inline" /></button>
              </div>
            </div>
          )}
        </>
      )}

      <NewFeePlanDrawer open={newOpen} onClose={() => setNewOpen(false)} token={token}
        onSuccess={(r) => { setNewOpen(false); if (r?.id) navigate(`/school/fee-plans/${r.id}`); else load(); }}
        formatMoney={formatMoney} currencySymbol={currencySymbol} />
    </div>
  );
}
